import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import MapPickerModal from './MapPickerModal';
import { BACKEND_URL } from '../config/api';

/**
 * AddBusinessPinModal - Simplified business pin creation for admins
 * 
 * Requirements:
 * - ONLY location selection (map picker)
 * - No name, description, or amenities required
 * - Creates virgin pin that admin must populate later
 * - Forces admins to immediately add content
 */

const AddBusinessPinModal = ({ isOpen, onClose, onPinCreated }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [pinCategory, setPinCategory] = useState('cafe'); // Business pin type - default to cafe
  const [businessName, setBusinessName] = useState(''); // Business name for identification

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
    setShowMapPicker(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!businessName.trim()) {
      toast.error('Please enter a business name');
      return;
    }

    if (businessName.trim().length < 2) {
      toast.error('Business name must be at least 2 characters');
      return;
    }

    if (businessName.trim().length > 100) {
      toast.error('Business name must be less than 100 characters');
      return;
    }

    if (!selectedLocation) {
      toast.error('Please select a location on the map');
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      
      // Create business pin with business name for identification
      const businessPinData = {
        name: businessName.trim(), // Use business name as park name
        business_name: businessName.trim(), // Store business name explicitly
        description: '',
        latitude: selectedLocation.lat,
        longitude: selectedLocation.lng,
        amenities: [],
        location: {
          country: selectedLocation.country || 'Unknown',
          city: selectedLocation.city || 'Unknown',
          suburb: selectedLocation.suburb || '',
          region: selectedLocation.region || ''
        }
      };

      // Category must be passed as query parameter, not in body
      const response = await axios.post(
        `${BACKEND_URL}/api/parks?category=${pinCategory}`,
        businessPinData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      toast.success('Business pin created! Click the pin to add offer details.');
      
      // Call parent callback with new pin data including zoom
      if (onPinCreated) {
        const pinDataWithZoom = {
          ...response.data,
          zoom: selectedLocation.zoom || 15
        };
        onPinCreated(pinDataWithZoom);
      }

      // Reset and close
      setSelectedLocation(null);
      setBusinessName('');
      onClose();

    } catch (error) {
      console.error('Error creating business pin:', error);
      
      if (error.response?.status === 401) {
        toast.error('Please log in to create business pins');
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to create business pin');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Main Modal */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-900">Add Business Pin</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={isSubmitting}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <p className="text-sm text-gray-600 mb-6">
            Place a business pin on the map. You'll add offer details after placement.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Pin Category Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                📌 Business Pin Type *
              </label>
              <select
                value={pinCategory}
                onChange={(e) => setPinCategory(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="cafe">☕ Café (Brown)</option>
                <option value="bar">🍷 Bar (Purple)</option>
                <option value="vet">➕ Vet (Red)</option>
                <option value="dog_food">🥣 Dog Food (Orange)</option>
                <option value="grooming">✂ Grooming (Pink)</option>
                <option value="accessories">🎾 Accessories (Yellow)</option>
                <option value="dog_shelter">🏠 Dog Shelter (Gray)</option>
                <option value="daycare">☀️ Daycare (Teal)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Choose the pin category for this business location
              </p>
            </div>

            {/* Business Name Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                🏢 Business Name *
              </label>
              <input
                type="text"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="e.g., Happy Paws Grooming"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                maxLength={100}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                This name will identify the business in admin panel and business portal
              </p>
            </div>

            {/* Location Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                📍 Business Location *
              </label>
              
              <button
                type="button"
                onClick={() => setShowMapPicker(true)}
                className="w-full px-4 py-3 border-2 border-emerald-500 rounded-lg text-emerald-600 font-medium hover:bg-emerald-50 transition-colors"
              >
                {selectedLocation ? '✓ Location Selected' : '📍 Select Location on Map'}
              </button>

              {selectedLocation && (
                <div className="mt-2 p-3 bg-emerald-50 rounded-lg text-sm">
                  <p className="text-emerald-800 font-medium">
                    📌 {selectedLocation.city}, {selectedLocation.country}
                  </p>
                  <p className="text-emerald-600 text-xs mt-1">
                    {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
                  </p>
                </div>
              )}
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                💡 <strong>Next step:</strong> After placing the pin, click it to open the template editor and add:
              </p>
              <ul className="mt-2 text-sm text-blue-700 space-y-1 ml-4">
                <li>• Special offer details</li>
                <li>• Business name</li>
                <li>• Terms & conditions</li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!businessName.trim() || !selectedLocation || isSubmitting}
                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Creating...' : 'Create Pin'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Map Picker Modal */}
      <MapPickerModal
        isOpen={showMapPicker}
        onConfirm={handleLocationSelect}
        onClose={() => setShowMapPicker(false)}
        pinCategory={pinCategory}
        initialCoordinates={{ lat: -36.8485, lng: 174.7633 }}
      />
    </>
  );
};

export default AddBusinessPinModal;
